import { useState } from 'react';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AutorenewIcon from '@mui/icons-material/Autorenew';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import SoftTypography from 'components/SoftTypography';

import { interpolateTemplate } from 'lib/i18n/dictionary';
import {
  buildClientShareText,
  newGalleryPasswordRequest,
  reissueGalleryLinkRequest,
  type Gallery,
} from 'gallery';

import type { GalleryAction } from './gallery-page';
import {
  ConfirmDialog,
  copyToClipboard,
  FieldLabel,
  notifyGalleryError,
  notifyGallerySuccess,
  useGalleryText,
} from './gallery-ui';

interface GalleryShareCardProps {
  gallery: Gallery;
  token?: string;
  run: GalleryAction;
}

export function GalleryShareCard({ gallery, token, run }: GalleryShareCardProps) {
  const { t } = useGalleryText();
  const [isReissueOpen, setIsReissueOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const link = gallery.link?.trim() || null;

  const copyForClient = async () => {
    const text = buildClientShareText(gallery, {
      link: t.share.linkTemplate,
      password: t.share.passwordTemplate,
    });
    if (await copyToClipboard(text)) {
      notifyGallerySuccess(t.title, t.share.copied);
    } else {
      notifyGalleryError(t.title, t.share.copyFailed);
    }
  };

  const reissue = async () => {
    setBusy(true);
    await run(() => reissueGalleryLinkRequest(gallery.uuid, token), { success: t.share.reissued });
    setBusy(false);
    setIsReissueOpen(false);
  };

  const newPassword = async () => {
    setBusy(true);
    await run(() => newGalleryPasswordRequest(gallery.uuid, token), {
      success: t.share.newPasswordDone,
    });
    setBusy(false);
  };

  return (
    <Card sx={{ p: 2 }} data-testid="gallery-share">
      <SoftTypography variant="overline" fontWeight="bold">
        {t.share.title}
      </SoftTypography>
      <SoftBox display="flex" flexDirection="column" gap={1.5}>
        {link ? (
          <SoftBox>
            <FieldLabel htmlFor="gallery-share-link">{t.share.linkLabel}</FieldLabel>
            <SoftInput
              id="gallery-share-link"
              value={link}
              readOnly
              inputProps={{ 'data-testid': 'gallery-share-link' }}
              onFocus={(event: React.FocusEvent<HTMLInputElement>) => event.target.select()}
            />
          </SoftBox>
        ) : (
          <SoftTypography
            variant="button"
            fontWeight="regular"
            color="text"
            data-testid="gallery-share-no-link"
          >
            {t.share.noLink}
          </SoftTypography>
        )}
        <SoftBox display="flex" gap={1} flexWrap="wrap">
          <SoftButton
            variant="gradient"
            color="info"
            onClick={copyForClient}
            disabled={!link}
            data-testid="gallery-copy-for-client"
          >
            {t.share.copyForClient}
          </SoftButton>
          <SoftButton
            variant="outlined"
            color="info"
            component="a"
            href={link ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!link}
            data-testid="gallery-open-as-guest"
          >
            {t.share.openAsGuest}
          </SoftButton>
          <SoftButton
            variant="outlined"
            color="error"
            onClick={() => setIsReissueOpen(true)}
            disabled={busy}
            data-testid="gallery-reissue-link"
          >
            {t.share.reissue}
          </SoftButton>
        </SoftBox>
        {gallery.passwordEnabled && gallery.password ? (
          <SoftBox
            display="flex"
            alignItems="center"
            gap={0.5}
            data-testid="gallery-share-password"
          >
            <SoftTypography variant="button" fontWeight="regular">
              <b>{interpolateTemplate(t.share.password, { password: gallery.password })}</b>
              {` · ${t.share.passwordHint}`}
            </SoftTypography>
            <Tooltip title={t.share.newPassword}>
              <span>
                <IconButton
                  size="small"
                  aria-label={t.share.newPassword}
                  onClick={newPassword}
                  disabled={busy}
                  data-testid="gallery-new-password"
                >
                  <AutorenewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </SoftBox>
        ) : (
          <SoftTypography variant="caption" color="text" data-testid="gallery-share-no-password">
            {t.share.noPassword}
          </SoftTypography>
        )}
      </SoftBox>
      <ConfirmDialog
        open={isReissueOpen}
        title={t.share.reissueTitle}
        body={t.share.reissueBody}
        confirmLabel={t.share.reissueConfirm}
        cancelLabel={t.cancel}
        busy={busy}
        testId="gallery-reissue-dialog"
        onCancel={() => setIsReissueOpen(false)}
        onConfirm={reissue}
      />
    </Card>
  );
}
